import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, AlertTriangle, Loader2, ShieldCheck, FileCheck, LogIn } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useCapsLockWarning } from '../hooks/useCapsLockWarning'
import { sanitizeInput } from '../utils/sanitize'

function LoginPage() {
  const { t } = useTranslation()
  const [gebruikersnaam, setGebruikersnaam] = useState('')
  const [wachtwoord, setWachtwoord] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [needs2FA, setNeeds2FA] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { capsLockOn, onKeyDown } = useCapsLockWarning()
  const [needsPrivacyPolicy, setNeedsPrivacyPolicy] = useState(false)
  const [ppToken, setPpToken] = useState(null)
  const [ppAccepting, setPpAccepting] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const login = useAuthStore((state) => state.login)
  const acceptPrivacyPolicy = useAuthStore((state) => state.acceptPrivacyPolicy)
  const navigate = useNavigate()

  // Ruim stale auth state op bij mount — voorkomt redirect-loop
  // Gebruik direct localStorage i.p.v. logout() om race conditions te vermijden
  useEffect(() => {
    localStorage.removeItem('orion-auth')
    useAuthStore.setState({ user: null, token: null, isAuthenticated: false })
    delete (window).__ORION_AUTH_CLEARED__
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (loading) return
    setLoading(true)
    const safeUsername = sanitizeInput(gebruikersnaam)
    try {
      const result = await login(safeUsername, wachtwoord, needs2FA ? totpCode : undefined)

      if (result.success) {
        const user = useAuthStore.getState().user
        // Hard redirect (niet React Router navigate) — zorgt ervoor dat
        // de hele app opnieuw mount met de verse auth state uit localStorage
        if (!user?.is_setup_completed) {
          window.location.href = '/setup'
          return
        }
        if (user?.role === 'INSTALLER') {
          window.location.href = '/mobile/worker'
        } else {
          window.location.href = '/dashboard'
        }
      } else if (result.code === 'PRIVACY_POLICY_REQUIRED') {
        setPpToken(result.ppToken)
        setNeedsPrivacyPolicy(true)
        setError('')
      } else if (result.code === '2FA_CODE_REQUIRED') {
        setNeeds2FA(true)
        setError('')
      } else if (result.code === '2FA_SETUP_REQUIRED') {
        setError(result.error || 'U moet eerst 2FA instellen via /instellingen/beveiliging')
      } else {
        console.error('[LOGIN] Login failed:', result.error)
        setError(result.error || t('login.loginFailed'))
      }
    } catch (err) {
      console.error('[LOGIN] Unexpected error during login:', err)
      setError(t('login.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptPrivacyPolicy = async () => {
    setPpAccepting(true)
    setError('')
    try {
      const result = await acceptPrivacyPolicy(ppToken)
      if (result.success) {
        // PP geaccepteerd — opnieuw inloggen zodat backend een volledig token geeft
        setNeedsPrivacyPolicy(false)
        setPpToken(null)
        const safeUsername = sanitizeInput(gebruikersnaam)
        const loginResult = await login(safeUsername, wachtwoord, needs2FA ? totpCode : undefined)
        if (loginResult.success) {
          const user = useAuthStore.getState().user
          if (!user?.is_setup_completed) {
            window.location.href = '/setup'
            return
          }
          if (user?.role === 'INSTALLER') {
            window.location.href = '/mobile/worker'
          } else {
            window.location.href = '/dashboard'
          }
        } else {
          setError(loginResult.error || t('login.loginFailed'))
        }
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError('Er ging iets mis bij het accepteren van de privacyverklaring.')
    } finally {
      setPpAccepting(false)
    }
  }

  if (needsPrivacyPolicy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas dark:bg-gray-900 p-4">
        <div className="max-w-lg w-full space-y-6 r11-card r11-card-pad shadow-xl">
          <div className="text-center">
            <FileCheck className="mx-auto w-12 h-12 text-accent" />
            <h2 className="mt-4 text-2xl font-bold text-ink dark:text-white">{t('login.privacy.title')}</h2>
            <p className="mt-2 text-sm text-muted">
              {t('login.privacy.acceptRequired')}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-card text-sm">
              {error}
            </div>
          )}

          <div className="bg-canvas dark:bg-gray-900 border border-line dark:border-gray-700 rounded-card p-4 text-sm text-ink-2 dark:text-gray-200 max-h-60 overflow-y-auto">
            <p className="font-medium mb-2">{t('login.privacy.gdprIntro')}</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>{t('login.privacy.purposeLimited')}</li>
              <li>{t('login.privacy.rights')}</li>
              <li>{t('login.privacy.encryption')}</li>
              <li>{t('login.privacy.contactAdmin')}</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => { setNeedsPrivacyPolicy(false); setPpToken(null); setError('') }}
              className="btn-ghost flex-1"
            >
              {t('login.privacy.cancel')}
            </button>
            <button
              onClick={handleAcceptPrivacyPolicy}
              disabled={ppAccepting}
              className="btn-primary flex-1 disabled:opacity-50"
            >
              {ppAccepting && <Loader2 className="w-4 h-4 animate-spin" />}
              {ppAccepting ? 'Verwerken...' : 'Ik ga akkoord'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas dark:bg-gray-900 p-4">
      <div className="max-w-md w-full space-y-8 r11-card r11-card-pad shadow-xl">
        <div>
          <h2 className="text-3xl font-bold text-center text-ink dark:text-white">
            {t('login.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-muted">
            {t('login.subtitle')}
          </p>
        </div>

        <div className="text-center mb-4">
          <Link to="/reset-password" className="text-sm text-accent hover:underline">
            {t('login.forgotPassword')}
          </Link>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-card text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="gebruikersnaam" className="r11-label">
                {t('login.username')}
              </label>
              <input
                id="gebruikersnaam"
                name="gebruikersnaam"
                type="text"
                required
                disabled={needs2FA}
                value={gebruikersnaam}
                onChange={(e) => setGebruikersnaam(e.target.value)}
                className="r11-input disabled:bg-gray-100 dark:disabled:bg-gray-700"
              />
            </div>

            <div>
              <label htmlFor="wachtwoord" className="r11-label">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  id="wachtwoord"
                  name="wachtwoord"
                  type={showPassword ? 'text' : 'password'}
                  required
                  disabled={needs2FA}
                  value={wachtwoord}
                  onChange={(e) => setWachtwoord(e.target.value)}
                  onKeyDown={onKeyDown}
                  className="r11-input pr-10 disabled:bg-gray-100 dark:disabled:bg-gray-700"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-faint hover:text-ink-2 transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset rounded-r-control"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {capsLockOn && (
                <div className="mt-2 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-card px-3 py-2 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{t('login.capsLockWarning')}</span>
                </div>
              )}
            </div>

            {needs2FA && (
              <div>
                <label htmlFor="totpCode" className="r11-label">
                  <span className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-accent" />
                    Authenticator code
                  </span>
                </label>
                <input
                  id="totpCode"
                  name="totpCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="r11-input text-center text-2xl tracking-[0.5em] font-mono"
                />
                <p className="mt-1 text-xs text-muted">
                  {t('login.twoFactor.codePrompt')}
                </p>
                <button
                  type="button"
                  onClick={() => { setNeeds2FA(false); setTotpCode(''); setError('') }}
                  className="mt-2 text-sm text-accent hover:underline"
                >
                  {t('login.twoFactor.backToLogin')}
                </button>
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {loading ? t('login.submitting') : needs2FA ? t('login.twoFactor.verify') : t('login.submit')}
            </button>
          </div>
        </form>
        <div className="mt-4 text-center text-sm text-muted space-y-1">
          <p><a href="/registreer" className="text-accent hover:underline">{t('login.registerLink')}</a></p>
          <p><a href="/wachtwoord-vergeten" className="text-faint hover:underline">{t('login.forgotPasswordLink')}</a></p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
